import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalList, LegalPage, LegalSection } from "@/components/LegalPage";

export const Route = createFileRoute("/cookies")({
  component: CookiesPage,
});

function CookiesPage() {
  return (
    <LegalPage
      title="Cookie Policy"
      effective="CREDIKING FINANCIAL SERVICE LIMITED · Last updated: 22nd August, 2026"
    >
      <p>
        This Cookie Policy explains how Crediking Financial Service Limited, a company incorporated
        under the laws of the Federal Republic of Nigeria and licensed and regulated as a money
        lender / digital lender and financial services provider, uses cookies and similar technologies
        on our website and mobile application (the “Platform”).
      </p>
      <p>
        This Policy should be read together with our{" "}
        <Link to="/privacy" className="text-gold hover:underline">
          Privacy Policy
        </Link>
        . It is designed to comply with the Nigeria Data Protection Act, 2023 (NDPA), the General
        Application and Implementation Directive (GAID) 2025 issued by the Nigeria Data Protection
        Commission (NDPC), and other applicable Nigerian laws.
      </p>

      <LegalSection title="1. What Are Cookies?">
        <p>
          Cookies are small text files placed on your device when you visit or use our Platform.
          Similar technologies include pixels, web beacons, local storage, and device identifiers.
          Cookies help us operate the Platform securely, remember your preferences, analyse usage,
          and — with your consent — improve our services or deliver relevant information. Some cookies
          may collect personal data, which is processed in accordance with the NDPA.
        </p>
      </LegalSection>

      <LegalSection title="2. How We Use Cookies">
        <LegalList
          items={[
            <>
              Ensuring the Platform functions securely and reliably (especially for loan applications,
              account access, and fraud prevention).
            </>,
            <>Remembering your preferences and settings.</>,
            <>Understanding how users interact with the Platform so we can improve it.</>,
            <>Measuring the effectiveness of our communications or delivering relevant content.</>,
          ]}
        />
        <p>
          We do not use cookies to collect sensitive personal data beyond what is necessary, and
          strictly necessary cookies do not process sensitive or financial data in a manner that
          requires consent under the GAID.
        </p>
      </LegalSection>

      <LegalSection title="3. Types of Cookies We Use">
        <p className="text-foreground/80 font-medium">3.1 Strictly necessary / essential cookies</p>
        <p>
          Required for the Platform to function (security, session management, authentication, fraud
          prevention, load balancing, and accessibility). They do not require consent under the
          NDPA/GAID, provided they do not process sensitive or financial data beyond core
          functionality.
        </p>
        <p className="text-foreground/80 font-medium">3.2 Functional / preference cookies (consent required)</p>
        <p>
          Remember choices you make (e.g. language, region, or form progress) to provide a more
          personalised experience.
        </p>
        <p className="text-foreground/80 font-medium">3.3 Analytics / performance cookies (consent required)</p>
        <p>
          Help us understand how visitors use the Platform (pages visited, time spent, errors) so we
          can improve performance and user experience. Data is typically aggregated.
        </p>
        <p className="text-foreground/80 font-medium">3.4 Marketing / advertising cookies (consent required, if used)</p>
        <p>
          May be used to deliver relevant information about our loan products or measure campaign
          effectiveness. We currently do not use these. If introduced, they will only be set after
          explicit consent.
        </p>
        <p className="text-foreground/80 font-medium">3.5 First-party vs third-party cookies</p>
        <p>
          Most cookies are set by us (first-party). Some may be set by trusted third-party service
          providers (e.g. analytics or security). We require such providers to comply with applicable
          data protection laws.
        </p>
      </LegalSection>

      <LegalSection title="4. Cookie Consent">
        <p>In line with the NDPA and GAID 2025:</p>
        <LegalList
          items={[
            <>Strictly necessary cookies are set automatically.</>,
            <>
              All other (non-essential) cookies require your prior, freely given, specific, informed,
              and unambiguous opt-in consent.
            </>,
            <>
              When you first visit the Platform, a clear cookie notice appears with options to Accept,
              Reject non-essential cookies, or Manage Preferences.
            </>,
            <>Pre-ticked boxes or implied consent through continued browsing are not used.</>,
            <>You may withdraw or change your consent at any time as easily as you gave it.</>,
          ]}
        />
      </LegalSection>

      <LegalSection title="5. How to Manage or Withdraw Consent">
        <LegalList
          items={[
            <>Use the cookie banner on your first visit, or clear site data and revisit to see it again.</>,
            <>
              Adjust your browser or device settings to block or delete cookies. Note that blocking
              essential cookies may prevent login or loan applications from working properly.
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection title="6. Duration of Cookies">
        <LegalList
          items={[
            <>Session cookies: deleted when you close your browser or app.</>,
            <>
              Persistent cookies: remain for a set period (from a few hours up to 1–2 years depending
              on purpose) or until you delete them.
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection title="7. Your Rights Under the NDPA">
        <LegalList
          items={[
            <>Right to be informed</>,
            <>Right of access</>,
            <>Right to rectification</>,
            <>Right to erasure (in certain circumstances)</>,
            <>Right to restrict or object to processing</>,
            <>Right to data portability</>,
            <>Right to withdraw consent</>,
            <>Right to lodge a complaint with the Nigeria Data Protection Commission (NDPC)</>,
          ]}
        />
        <p>To exercise these rights regarding cookie-related data, contact us using the details below.</p>
      </LegalSection>

      <LegalSection title="8. Updates to This Policy">
        <p>
          We may update this Cookie Policy from time to time. The updated version will be posted on
          the Platform with a revised “Last Updated” date. Material changes will be notified via the
          Platform or other appropriate means.
        </p>
      </LegalSection>

      <LegalSection title="9. Contact Us">
        <p>Crediking Financial Service Limited</p>
        <p>Data Protection Officer / Privacy Contact</p>
        <p>Email: credikingltd@gmail.com</p>
        <p>Phone: 09122290559</p>
        <p>
          You may also contact the Nigeria Data Protection Commission (NDPC) at ndpc.gov.ng if you
          believe your rights have been infringed.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
